import React, { useState } from 'react';
import {
  GridLegacy as Grid,
  Typography,
  Box,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import image from './essay-craft.jpg'; // Ensure path is correct
import ReadMoreText from './ReadMoreText';

const features = [
  {
    title: 'Grading Bias Detection & Predictive Modeling',
    content:
      'Machine learning and NLP-driven analysis of graded assignments extract semantic trends and detect subtle instructor biases, uncovering nuanced patterns that often elude the human eye. Predictive modeling then forecasts likely criticisms, enabling students to revise strategically before submission.',
  },
  {
    title: 'Precision Revisions to Match Instructor Preferences',
    content:
      'EssayCraft applies predictive insights to generate revision suggestions calibrated to each instructor’s subjective grading criteria. With fast approvals and course-specific workspaces, the editing process is streamlined and deeply personalized.',
  },
  {
    title: 'Adaptive Feedback for Every Assignment',
    content:
      "Further refine EssayCraft's recommendations by sharing your prompt, assignment type, and rubric. Adjustment proposals are configured to fulfill the structural, rhetorical, and evaluative requirements specific to the task at hand.",
  },
  {
    title: 'Presets for Genre & Stylistic Transformation',
    content:
      'Select from a range of academic or creative genre presets to instantly reframe your existing draft. EssayCraft adapts tone, structure, and stylistic markers to align with the conventions of your chosen format—ensuring clarity, cohesion, and genre fidelity without rewriting from scratch.',
  },
  {
    title: 'Interactive AI Writing Assistant',
    content:
      "Use EssayCraft's Chat interface to ask questions, clarify feedback, and understand the reasoning behind suggested changes. Track your progress over time with dynamic, conversational support to help you achieve your writing goals.",
  },
];

const FeatureEssayCraft: React.FC = () => {
  const [hovered, setHovered] = useState<number | null>(null);
  const [clicked, setClicked] = useState<number | null>(null);

  return (
    <Box sx={{
      backgroundColor: 'transparent',
      minHeight: { xs: 'auto', md: '100vh' },
      py: { xs: 4, md: 8 },
      px: { xs: 2, sm: 4, md: 8, xl: 16 }
    }}>
      <Grid
        container
        spacing={{ xs: 2, md: 4 }}
        sx={{
          minHeight: '100%',
          alignItems: 'stretch',
        }}
      >
        {/* Left Content */}
        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
              lineHeight: { xs: 1.2, md: 1.3 }
            }}
          >
            EssayCraft AI
          </Typography>

          <Typography 
            variant="h5" 
            sx={{ 
              mt: { xs: 1.5, md: 2 }, 
              fontWeight: 'bold',
              fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' },
              lineHeight: { xs: 1.3, md: 1.4 },
              width: '100%',
            }}
          >
						Navigate subjective grading standards with precision
          </Typography>

          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: { xs: '1rem', md: '1.1rem' }, 
              mb: { xs: 2, md: 3 },
              lineHeight: { xs: 1.4, md: 1.5 },
              width: '100%',
              maxWidth: '65ch',
            }}
          >
            Adapt your writing to meet diverse teacher preferences. <br />
            Revision suggestions tailored to each class and audience.
          </Typography>

          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 'bold', 
              mb: 1,
              fontSize: { xs: '1rem', md: '1.1rem' },
              maxWidth: { xs: '65ch', md: '65ch' }
            }}
          >
            What Makes EssayCraft Different?
          </Typography>
          <ReadMoreText>
            EssayCraft isn’t designed to replace generative AI models—it complements them.
            While most writing tools are trained on generalized standards of “good writing”,
            EssayCraft is engineered to navigate the subjectivity of grading. Our system adapts
            revision proposals to reflect individual instructors’ tastes and evaluative tendencies.
            <br />
            <br />
            Whether your draft was written independently or with the help of another AI model,
            EssayCraft serves as an intelligent proofreader—fine-tuning your work to meet
            rubric-specific, instructor-driven, and disciplinary demands.
          </ReadMoreText>

          {/* Feature List with arrows, no divider */}
          {features.map((feature, idx) => {
            const isExpanded = clicked === idx || hovered === idx; // Open on click or hover
            return (
              <Box
                key={idx}
                sx={{
                  mt: { xs: 1.5, md: 2 },
                  pb: 1,
                  cursor: 'pointer',
                }}
                onMouseEnter={() => setHovered(idx)} // Desktop hover
                onMouseLeave={() => setHovered(null)} // Desktop hover
                onClick={() => {
                  // Mobile/Desktop click toggle
                  if (clicked === idx) {
                    setClicked(null); // Close if already open
                  } else {
                    setClicked(idx); // Open this one
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    minHeight: { xs: 44, md: 'auto' }, // Minimum touch target size
                  }}
                >
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 'bold',
                      fontSize: { xs: '0.95rem', md: '1rem' },
                      lineHeight: { xs: 1.3, md: 1.4 },
                      maxWidth: { xs: '80%', md: '85%' } // Prevent text from overlapping button
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <IconButton
                    size="small"
                    disableRipple
                    sx={{
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease-in-out',
                      minWidth: { xs: 44, md: 'auto' }, // Minimum touch target size
                      minHeight: { xs: 44, md: 'auto' },
                    }}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                </Box>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mt: 1,
                      fontSize: { xs: '0.85rem', md: '0.9rem' },
                      lineHeight: { xs: 1.4, md: 1.5 },
                      maxWidth: { xs: '100%', md: '95%' }
                    }}
                  >
                    {feature.content}
                  </Typography>
                </Collapse>
              </Box>
            );
          })}
        </Grid>

        {/* Right Image */}
        <Grid item xs={12} md={6} sx={{ display: 'flex', order: { xs: -1, md: 0 } }}>
					<Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",

                        height: {
                            xs: '250px', // Fixed height for mobile
                            sm: '400px',
                            md: '500px',
                            lg: '600px',
                        },

                        width: '100%',
                        mt: { xs: 0, md: 0 },
                        mb: { xs: 2, md: 0 },
                        overflow: 'hidden',
                      }}
                    >
            <Box
              component="img"
              src={image}
              sx={{
								width: '100%',
								height: '100%',
                objectFit: 'cover',
                borderRadius: { xs: 1, md: 2 },
                boxShadow: { xs: 2, md: 3 },
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FeatureEssayCraft;
